import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Badge,
    Box,
    Button,
    Fab,
    Grow,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import jsPDF from 'jspdf';
import aiLoadingGif from '../../assets/AI_Loading.gif';
import { generateMEITDMessages, getMEITDSuggestedQuestions } from './MEITDAIHelpers';

// ── Stream parser ────────────────────────────────────────────────
function parseStream(reader, onToken, onDone, onError) {
    const decoder = new TextDecoder();
    let buffer = '';
    let done = false;

    function finish() {
        if (!done) {
            done = true;
            onDone();
        }
    }

    (function read() {
        reader
            .read()
            .then(({ done: streamDone, value }) => {
                if (streamDone) { finish(); return; }
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();
                lines.forEach((line) => {
                    if (!line.startsWith('data: ')) return;
                    const data = line.slice(6).trim();
                    if (data === '[DONE]') { finish(); return; }
                    try {
                        const content = JSON.parse(data).choices?.[0]?.delta?.content;
                        if (content) onToken(content);
                    } catch (_) { /* skip malformed lines */ }
                });
                read();
            })
            .catch((err) => { onError(err); });
    }());
}

// ── Markdown-lite renderer ───────────────────────────────────────
function MarkdownText({ text }) {
    const lines = text.split('\n');
    return (
        <Box sx={{ '& > *:first-of-type': { mt: 0 } }}>
            {lines.map((line, i) => {
                const trimmed = line.trim();
                if (trimmed.startsWith('### ')) {
                    return <Typography key={i} variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>{trimmed.slice(4)}</Typography>;
                }
                if (trimmed.startsWith('## ')) {
                    return <Typography key={i} variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5 }}>{trimmed.slice(3)}</Typography>;
                }
                if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
                    return (
                        <Box key={i} sx={{ display: 'flex', gap: 0.8, ml: 1.5, mb: 0.3 }}>
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>•</Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}><BoldInline text={trimmed.slice(2)} /></Typography>
                        </Box>
                    );
                }
                if (trimmed === '') return <Box key={i} sx={{ height: 8 }} />;
                return <Typography key={i} variant="body2" sx={{ lineHeight: 1.6, mb: 0.3 }}><BoldInline text={trimmed} /></Typography>;
            })}
        </Box>
    );
}

MarkdownText.propTypes = { text: PropTypes.string.isRequired };

function BoldInline({ text }) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return (
        <>
            {parts.map((part, i) =>
                part.startsWith('**') && part.endsWith('**')
                    ? <strong key={i}>{part.slice(2, -2)}</strong>
                    : part
            )}
        </>
    );
}

BoldInline.propTypes = { text: PropTypes.string.isRequired };

// ── Floating Chat Panel dimensions ───────────────────────────────
const PANEL_WIDTH = 400;
const PANEL_HEIGHT = 540;
const FAB_SIZE = 56;
const FAB_MARGIN = 24;

// ── Main Component ───────────────────────────────────────────────
export default function MEITDAIChatBox() {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [unread, setUnread] = useState(0);
    const scrollRef = useRef(null);

    const suggestedQuestions = useMemo(() => getMEITDSuggestedQuestions(), []);

    // Auto-scroll on new messages
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, loading]);

    // Clear unread when panel opens
    useEffect(() => {
        if (open) setUnread(0);
    }, [open]);

    const handleSend = useCallback(async (questionText) => {
        const prompt = (questionText || input).trim();
        if (!prompt) return;
        setInput('');
        setError('');
        setLoading(true);

        const userMsg = { role: 'user', text: prompt, time: new Date() };
        const aiMsg = { role: 'ai', text: '', time: new Date() };
        setMessages((prev) => [...prev, userMsg, aiMsg]);

        try {
            const response = await fetch('https://api.openai.com/v1/chat/completions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer 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',
                },
                body: JSON.stringify({
                    model: 'gpt-4o-mini',
                    temperature: 0.5,
                    stream: true,
                    messages: generateMEITDMessages(prompt),
                }),
            });

            if (!response.ok) throw new Error(`API error: ${response.status}`);

            let streamed = '';
            const reader = response.body.getReader();

            await new Promise((resolve, reject) => {
                parseStream(
                    reader,
                    (token) => {
                        streamed += token;
                        setMessages((prev) => {
                            const updated = [...prev];
                            updated[updated.length - 1] = { ...updated[updated.length - 1], text: streamed };
                            return updated;
                        });
                    },
                    () => {
                        setLoading(false);
                        setUnread((prev) => prev + 1);
                        resolve();
                    },
                    (err) => { setError('Stream error occurred.'); setLoading(false); reject(err); }
                );
            });
        } catch (e) {
            setError('Failed to get a response. Please try again.');
            setLoading(false);
        }
    }, [input]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && input.trim()) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleDownloadPDF = (msg) => {
        // eslint-disable-next-line new-cap
        const doc = new jsPDF();
        const margin = 14;
        let y = margin;
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('MEITD Talent Development — AI Analysis', margin, y);
        y += 10;
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(10);
        const lines = doc.splitTextToSize(msg.text, 180);
        lines.forEach((line) => {
            if (y + 8 > doc.internal.pageSize.height - margin) { doc.addPage(); y = margin; }
            doc.text(line, margin, y);
            y += 6;
        });
        doc.save('MEITD_AI_Analysis.pdf');
    };

    const handleReset = () => {
        setMessages([]);
        setInput('');
        setError('');
    };

    const formatTime = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const hasMessages = messages.length > 0;

    return (
        <>
            {/* ── Floating Action Button ───────────────────── */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: FAB_MARGIN,
                    right: FAB_MARGIN,
                    zIndex: 1300,
                }}
            >
                {!open && (
                    <Tooltip title="MEITD AI Assistant" placement="left" arrow>
                        <Badge
                            badgeContent={unread}
                            color="error"
                            overlap="circular"
                            sx={{ '& .MuiBadge-badge': { fontSize: 11, height: 20, minWidth: 20 } }}
                        >
                            <Fab
                                color="primary"
                                onClick={() => setOpen(true)}
                                sx={{
                                    width: FAB_SIZE,
                                    height: FAB_SIZE,
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.darker || theme.palette.primary.main} 100%)`,
                                        transform: 'scale(1.08)',
                                    },
                                    transition: 'transform 0.2s ease',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/assets/icons/ailogo.png`}
                                    alt="MEITD AI"
                                    sx={{ width: 34, height: 34, filter: 'invert(1)' }}
                                />
                            </Fab>
                        </Badge>
                    </Tooltip>
                )}
            </Box>

            {/* ── Floating Chat Panel ──────────────────────── */}
            <Grow in={open} style={{ transformOrigin: 'bottom right' }}>
                <Paper
                    elevation={16}
                    sx={{
                        position: 'fixed',
                        bottom: FAB_MARGIN,
                        right: FAB_MARGIN,
                        zIndex: 1301,
                        width: { xs: `calc(100vw - ${FAB_MARGIN * 2}px)`, sm: PANEL_WIDTH },
                        height: { xs: `calc(100vh - ${FAB_MARGIN * 2}px - 60px)`, sm: PANEL_HEIGHT },
                        maxHeight: `calc(100vh - ${FAB_MARGIN * 2}px)`,
                        borderRadius: 3,
                        overflow: 'hidden',
                        display: open ? 'flex' : 'none',
                        flexDirection: 'column',
                        border: `1px solid ${theme.palette.primary.light}`,
                    }}
                >
                    {/* ── Header bar ─────────────────────────── */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 2,
                            py: 1.2,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            color: '#fff',
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={`${process.env.PUBLIC_URL}/assets/icons/ailogo.png`}
                            alt="MEITD AI"
                            sx={{ width: 26, height: 26, mr: 1, filter: 'invert(1)' }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                MEITD AI Assistant
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.65rem' }}>
                                Powered by GPT-4o
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>

                    {/* ── Chat body ──────────────────────────── */}
                    <Box
                        ref={scrollRef}
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            px: 2,
                            py: 1.5,
                            bgcolor: theme.palette.grey[50],
                        }}
                    >
                        {/* Greeting + suggested questions */}
                        {!hasMessages && !loading && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                                    <Box component="img" src={`${process.env.PUBLIC_URL}/assets/icons/ailogo.png`} alt="MEITD AI" sx={{ width: 20, height: 20, mt: 0.3 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Hi! I can help you analyse <strong>SWIS workforce data</strong>, <strong>DOSM labour force statistics</strong>, <strong>skills gaps</strong>, and <strong>PCDS 2030 industry readiness</strong>. Ask me anything about Sarawak&apos;s talent ecosystem.
                                    </Typography>
                                </Box>
                                <Stack spacing={0.8}>
                                    {suggestedQuestions.map((q, i) => (
                                        <Button
                                            key={i}
                                            variant="outlined"
                                            size="small"
                                            startIcon={<HelpOutlineIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => handleSend(q)}
                                            sx={{
                                                textTransform: 'none',
                                                justifyContent: 'flex-start',
                                                textAlign: 'left',
                                                fontSize: '0.75rem',
                                                borderRadius: 1.5,
                                                py: 0.7,
                                                px: 1.5,
                                                borderColor: theme.palette.divider,
                                                color: 'text.primary',
                                                '&:hover': { borderColor: theme.palette.primary.main, bgcolor: theme.palette.primary.lighter || theme.palette.action.hover },
                                            }}
                                        >
                                            {q}
                                        </Button>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Messages */}
                        {messages.map((msg, idx) => {
                            const isUser = msg.role === 'user';
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: isUser ? 'flex-end' : 'flex-start',
                                        mb: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            maxWidth: '85%',
                                            p: 1.5,
                                            borderRadius: 2,
                                            position: 'relative',
                                            bgcolor: isUser ? theme.palette.primary.main : '#fff',
                                            color: isUser ? '#fff' : 'text.primary',
                                            border: isUser ? 'none' : `1px solid ${theme.palette.divider}`,
                                            boxShadow: isUser ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, opacity: 0.8 }}>
                                            {isUser ? 'You' : 'MEITD AI'}
                                        </Typography>

                                        {isUser ? (
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.text}</Typography>
                                        ) : (
                                            <MarkdownText text={msg.text || ''} />
                                        )}

                                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, opacity: 0.6, fontSize: '0.6rem' }}>
                                            {formatTime(msg.time)}
                                        </Typography>

                                        {/* Download button for AI messages */}
                                        {!isUser && msg.text && (
                                            <Tooltip title="Download as PDF" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDownloadPDF(msg)}
                                                    sx={{ position: 'absolute', top: 4, right: 4, opacity: 0.5, '&:hover': { opacity: 1 } }}
                                                >
                                                    <DownloadIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}

                        {/* Loading indicator */}
                        {loading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#fff', borderRadius: 2, border: `1px solid ${theme.palette.divider}`, maxWidth: '80%', mb: 1 }}>
                                <Box component="img" src={aiLoadingGif} alt="Loading..." sx={{ width: 28, height: 28, objectFit: 'contain' }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Analysing data...</Typography>
                            </Box>
                        )}

                        {/* Error */}
                        {error && (
                            <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: '0.8rem' }}>{error}</Typography>
                        )}
                    </Box>

                    {/* ── Input bar ──────────────────────────── */}
                    <Box sx={{ px: 1.5, py: 1, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: '#fff', flexShrink: 0 }}>
                        <Stack direction="row" spacing={0.8} alignItems="center">
                            {hasMessages && (
                                <Tooltip title="New conversation" arrow>
                                    <IconButton size="small" onClick={handleReset} sx={{ color: 'text.secondary' }}>
                                        <RefreshIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                placeholder="Ask about SWIS, DOSM, skills gaps..."
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={loading}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        fontSize: '0.82rem',
                                    },
                                }}
                            />
                            <IconButton
                                color="primary"
                                onClick={() => handleSend()}
                                disabled={!input.trim() || loading}
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    color: '#fff',
                                    '&:hover': { bgcolor: theme.palette.primary.dark },
                                    '&.Mui-disabled': { bgcolor: theme.palette.grey[300], color: theme.palette.grey[500] },
                                    borderRadius: 2,
                                    width: 36,
                                    height: 36,
                                }}
                            >
                                <SendIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Stack>
                    </Box>
                </Paper>
            </Grow>
        </>
    );
}
